import _ from 'lodash';
import MobileLeaveModal from 'components/Modal/MobileLeaveModal';
import { connectMindPaths, preciousChildPaths } from 'constants/path';
import useToggle from 'hooks/CustomHooks/useToggle';
import { useEffect, useState } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { useGovernmentHeaderStore } from 'store/useGovernmentHeaderStore';
import styled from 'styled-components';
import { TitleContainer } from 'styles/Global/Government/agreeAndCodeConfirmPageStyles';
import { BackgroundColor, BorderColor, PrimaryColor, TextColor } from 'styles/Global/colorSemantic';
import { Radius } from 'styles/Global/objectStyles';
import { Body3Medium, Body4SemiBold, Body5Medium, Caption1Regular, Title2SemiBold } from 'styles/Global/typography';
import { ShowAssessmentListType } from 'models/PreciousChild/inspectionGuidPageTypes';
import { MobileAssessmentButton } from './Components/AssessmentButton';
import { mindUpAssessmentApi } from 'api/MindUp/mindUpAssessmentApi';
import MobileBasicButton from 'components/Buttons/MobileBasicButton';
import { AssessmentStatus, DiagnosisStatus, VoucherCodeType } from 'utils/statusCode';
import IconCheckRoundFill from 'assets/Icons/Check/IconCheckRoundFill';
import GenderMan from 'assets/Icons/Gender/GenderMan';
import GenderWoman from 'assets/Icons/Gender/GenderWoman';
import getIntroduceModel from 'functions/MindUp/getIntroduceModel';
import { childReportByParent, childSelfReport, parentSelfReport } from 'constants/viewDetailDiagnosisPageConstants';
import { getRelationText } from 'utils/getTypeText';
import { DCKDiagnosisApi } from 'api/DCK/DCKDiagnosisApi';
import { StorageKeys } from 'constants/storageKeys';
import { mindUpDiagnosisApi } from 'api/MindUp/mindUpDiagnosisApi';

const InspectionGuidePage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { setHeaderText, setInit } = useGovernmentHeaderStore();

    const diagnosisInfo = state.diagnosis;
    const token = state.token;
    const voucher = state.voucher;

    const [buttonTitle, setButtonTitle] = useState('상담 예약하기');
    const [showAssessmentList, setShowAssessmentList] = useState<ShowAssessmentListType[]>([]);
    const [isLeaveModal, setIsLeaveModal] = useToggle();
    const [isAllComplete, setIsAllComplete] = useState(false);
    const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);
    const [isShowMoveModal, setIsShowMoveModal] = useToggle();

    const isAiVoucher = (voucher?.type ?? voucher?.data.voucher.type) === VoucherCodeType.MINDUPAI;

    // header text 입력 및 list filtering useEffect
    useEffect(() => {
        setHeaderText(<Body3Medium>검사 목록</Body3Medium>);
        const assessmentList = diagnosisInfo['assessmentList'];

        const assessmentChildrenList = _.filter(assessmentList, (item) => _.includes(childSelfReport, item.assessmentCode));
        const assessmentByParentsList = _.filter(assessmentList, (item) => _.includes(childReportByParent, item.assessmentCode));
        const assessmentParentsList = _.filter(assessmentList, (item) => _.includes(parentSelfReport, item.assessmentCode));

        setShowAssessmentList([...assessmentChildrenList, ...assessmentByParentsList, ...assessmentParentsList]);

        setButtonTitle((voucher?.data?.voucher?.type ?? voucher.type) === VoucherCodeType.MINDUPAI ? '검사 결과보기' : '상담 예약하기');

        return () => {
            setInit();
        };
    }, []);

    useEffect(() => {
        const hasNotCompleteTest = _.some(showAssessmentList, (e) => e.status < AssessmentStatus.completed);
        setIsAllComplete(hasNotCompleteTest);
    }, [showAssessmentList]);

    useEffect(() => {
        const hasCompletedTest = _.some(showAssessmentList, (e) => e.status === AssessmentStatus.completed);
        setHasCompletedAssessment(hasCompletedTest);
    }, [showAssessmentList]);

    // 뒤로 가기 기능 차단
    useBlocker(({ currentLocation, historyAction }) => {
        if ((voucher?.data?.voucher.type ?? voucher.type) === VoucherCodeType.MINDUPAI && historyAction === 'POP') {
            const encryptedKey = sessionStorage.getItem(StorageKeys.ENCRYPTED_DEVICE_KEY) ?? '';
            mindUpDiagnosisApi.getListByDeviceKey(encryptedKey).then((res) => {
                navigate(connectMindPaths.sungnamDiagnosisHistoryList, { replace: true, state: { diagnosisList: res.data.list, token: res.data.token } });
            });

            return false;
        }

        if (isLeaveModal) {
            return true;
        }

        if (currentLocation.pathname === preciousChildPaths.inspectionGuide && !isLeaveModal && historyAction === 'POP') {
            setIsLeaveModal();
            return true;
        }
        return false;
    });

    const clickStartButton = async () => {
        const changeStatusRqData = {
            _id: diagnosisInfo?._id ?? '',
            status: DiagnosisStatus.completed,
        };

        const response = await DCKDiagnosisApi.changeDiagnosisStatus(changeStatusRqData, token);

        if (response.status === 'ok') {
            if (!voucher || voucher.type < VoucherCodeType.MINDUPAI) {
                navigate(preciousChildPaths.counselReservation, { replace: true, state: { data: state.data, token: token, diagnosis: diagnosisInfo } });
            } else if (voucher.data.voucher.type === VoucherCodeType.MINDUPAI) {
                navigate(connectMindPaths.result, { replace: true, state: { diagnosis: diagnosisInfo, token: token, voucher: voucher } });
            }
        }
    };

    const formatPhoneNumber = (phoneNumber: string) => {
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');

        if (cleaned.length < 10) return cleaned;

        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7, 11)}`;
    };

    const parseBirthDay = (date: string) => {
        const cleaned = ('' + date).replace(/\D/g, '');

        if (cleaned.length < 6) return null;

        const year = cleaned.slice(0, 2); // 연도
        const month = cleaned.slice(2, 4); // 월
        const day = cleaned.slice(4, 6); // 일

        return `20${year}.${month}.${day}`;
    };

    useBlocker(({ currentLocation, historyAction }) => {
        if (historyAction === 'POP') {
            if (voucher.type && voucher.type !== VoucherCodeType.MINDUP) {
                clickLeaveButton();
                return true;
            } else if (voucher.data.voucher.type === VoucherCodeType.MINDUPAI) {
                setIsShowMoveModal();
            }
            return true;
        }
        return false;
    });

    // modal 나가기 버튼
    const clickLeaveButton = () => {
        setInit();
        setIsLeaveModal();
    };

    const modalTitle = hasCompletedAssessment ? '검사가 이미 완료되어' : '검사가 이미 지정되어';
    const modalSubTitle = hasCompletedAssessment ? '접근할 수 없습니다.' : '되돌아갈 수 없습니다.';
    const modalContent = hasCompletedAssessment ? '검사가 완료되어 이전 검사지로 다시 접근할 수 없습니다.' : '입력한 정보에 맞춰서 검사가 이미 지정되어 되돌아갈 수 없습니다.';

    return (
        <Layout>
            {isLeaveModal && <MobileLeaveModal title={modalTitle} subTitle={modalSubTitle} content={modalContent} setIsLeaveModal={setIsLeaveModal} firstButton='확인' firstButtonType='fill' />}
            {isShowMoveModal && isAiVoucher && (
                <MobileLeaveModal
                    title={'검사 목록으로 이동합니다.'}
                    content={`검사 목록으로 이동하시겠습니까?`}
                    firstButton='아니요'
                    firstButtonType='gray'
                    secondButton='이동하기'
                    secondButtonType='fill'
                    infoContentsPadding='0px'
                    infoContentsBorderColor={BackgroundColor.color_background_primary_neutral}
                    infoContentsBorderRadius='8px'
                    setIsLeaveModal={setIsShowMoveModal}
                    clickLeaveButtonInLeaveModal={async () => {
                        const encryptedKey = sessionStorage.getItem(StorageKeys.ENCRYPTED_DEVICE_KEY) ?? '';
                        const historyInfo = await mindUpDiagnosisApi.getListByDeviceKey(encryptedKey);
                        navigate(connectMindPaths.sungnamDiagnosisHistoryList, { replace: false, state: { diagnosisList: historyInfo.data.list, token: historyInfo.data.token } });
                    }}
                />
            )}

            {!_.isEmpty(showAssessmentList) && (
                <>
                    <TitleAndDescContainer>
                        <TitleContainer>
                            <>아래의 검사를 진행해주세요.</>
                        </TitleContainer>
                        <InspectionGuideContainer>
                            <InspectionTitleWrapper>
                                <IconWrapper>
                                    <IconCheckRoundFill $color={PrimaryColor.color_primary_neutral} $width={24} $height={24} />
                                </IconWrapper>
                                입력 정보
                            </InspectionTitleWrapper>
                            <DescContainer>
                                <DescWrapper>
                                    <TesterInfoTitle>자녀 이름</TesterInfoTitle>
                                    <InfoContentsContainer>
                                        <NameGenderWrapper>
                                            <Body5Medium>{diagnosisInfo.patientName}</Body5Medium>
                                            {diagnosisInfo.patientGender === 'female' ? <GenderWoman $width='16px' $height='16px' /> : <GenderMan $width='16px' $height='16px' />}
                                        </NameGenderWrapper>
                                    </InfoContentsContainer>
                                </DescWrapper>
                                <DescWrapper>
                                    <TesterInfoTitle>자녀 생년월일</TesterInfoTitle>
                                    <Body5Medium>{parseBirthDay(diagnosisInfo.patientIdentityNumber)}</Body5Medium>
                                </DescWrapper>
                                <DescWrapper>
                                    <TesterInfoTitle>자녀 학령학기</TesterInfoTitle>
                                    <Body5Medium>{diagnosisInfo.gradeName}</Body5Medium>
                                </DescWrapper>
                                <DescWrapper>
                                    <TesterInfoTitle>주양육자</TesterInfoTitle>
                                    <InfoContentsContainer>
                                        <RelationWrapper>
                                            <Body5Medium>{getRelationText(diagnosisInfo.relationType)}</Body5Medium>
                                            <Divide />
                                            <Body5Medium>{diagnosisInfo.parentName}</Body5Medium>
                                        </RelationWrapper>
                                    </InfoContentsContainer>
                                </DescWrapper>
                                <DescWrapper>
                                    <TesterInfoTitle>연락처</TesterInfoTitle>
                                    <Body5Medium>{formatPhoneNumber(diagnosisInfo.patientMobile)}</Body5Medium>
                                </DescWrapper>
                            </DescContainer>
                        </InspectionGuideContainer>
                    </TitleAndDescContainer>
                    <AssessmentListContainer>
                        {showAssessmentList.map((assessment, index) => {
                            const getAssessmentType = () => {
                                if (_.includes(parentSelfReport, assessment.assessmentCode)) {
                                    return assessmentType.forParents;
                                }

                                if (_.includes(childReportByParent, assessment.assessmentCode)) {
                                    return assessmentType.byParents;
                                }

                                return assessmentType.byChild;
                            };

                            return (
                                <MobileAssessmentButton
                                    $title={assessment.group}
                                    $subTitle={''}
                                    $desc={getIntroduceModel(assessment.assessmentCode)?.testTitle ?? ''}
                                    $label={getAssessmentType()}
                                    $type={getAssessmentType()}
                                    // $subName={getIntroduceModel(assessment.assessmentCode)?.testTarget}
                                    $subName={assessment.subName}
                                    $bedgeType='strong'
                                    $number={index + 1}
                                    $assessmentStatus={assessment.status}
                                    $diagnosisStatus={diagnosisInfo.status}
                                    isDisable={false}
                                    $onClick={async () => {
                                        try {
                                            const response = await mindUpAssessmentApi.getAssessmentScriptByCode(assessment.assessmentCode, token);

                                            if (response.status === 'ok' && response.data) {
                                                const nextUrl = voucher?.data ? connectMindPaths.introduce : preciousChildPaths.introduce;
                                                navigate(nextUrl, {
                                                    replace: false,
                                                    state: {
                                                        assessmentCode: assessment.assessmentCode,
                                                        question: response.data,
                                                        token: token,
                                                        diagnosisInfo: diagnosisInfo,
                                                        selectedAssessmentInfo: assessment,
                                                        voucher: voucher,
                                                    },
                                                });
                                            }
                                        } catch (error) {}
                                    }}
                                />
                            );
                        })}
                    </AssessmentListContainer>
                    <ButtonWrapper>
                        <MobileBasicButton onClick={clickStartButton} $type='fill' $gap='4px' disabled={isAllComplete}>
                            {buttonTitle}
                        </MobileBasicButton>
                    </ButtonWrapper>
                </>
            )}
        </Layout>
    );
};

export default InspectionGuidePage;

export const assessmentType = {
    byChild: '자녀용',
    byParents: '부모보고용',
    forParents: '부모용',
} as const;

const Layout = styled.section`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0px 16px;
`;

const TitleAndDescContainer = styled(Title2SemiBold)`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const InfoContentsContainer = styled(Caption1Regular)`
    display: flex;
    flex-wrap: wrap;
    color: ${TextColor.color_text_light};
    padding-top: 2px;
`;

const InspectionGuideContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 4px;
    border-radius: ${Radius.radius_8};
    padding: 12px 16px 14px 12px;
    background-color: ${BackgroundColor.color_background_primary_neutral};
`;

const InspectionTitleWrapper = styled(Body4SemiBold)`
    display: flex;
    height: 24px;
    align-items: center;
    gap: 4px;
`;

const DescContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 28px;
    gap: 4px;
`;

const DescWrapper = styled.div`
    display: flex;
    gap: 14px;
    align-items: center;
    justify-content: start;
`;

const TesterInfoTitle = styled(Caption1Regular)`
    width: 80px;
    min-width: 80px;
    color: ${TextColor.color_text_light};
`;

const AssessmentListContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 24px;
    gap: 8px;
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 3px;
`;

const ButtonWrapper = styled.div`
    display: flex;
    padding-top: 32px;
`;

const NameGenderWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
`;

const RelationWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const Divide = styled.div`
    width: 1px;
    height: 12px;
    background-color: ${BorderColor.color_border_normal};
    opacity: 0.3;
`;
